import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import {
  User,
  AnalystRoleEnum,
  TasksService,
  QualityRequestOrigin,
  Task,
} from '@compumark/analyst-selection-tool-client';
import { UserService } from 'src/app/security/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmQualityDialogComponent } from './confirm-quality-dialog/confirm-quality-dialog/confirm-quality-dialog.component';
import { first } from 'rxjs';
@Component({
  selector: 'app-quality-icon-renderer',
  standalone: false,
  templateUrl: './quality-icon-renderer.component.html',
  styleUrls: ['./quality-icon-renderer.component.scss'],
})
export class QualityIconRendererComponent implements ICellRendererAngularComp {
  readonly tooltips: { [key: string]: string } = {
    REQUIRED_BY_ANALYST_AND_CLIENT:
      'A quality check is required for the analyst and for the client',
    REQUIRED_BY_CLIENT: 'A quality check is required for the client',
    REQUIRED_BY_ANALYST: 'A quality check is required for the analyst',
    REQUESTED_BY_ANALYST: 'A quality check has been requested by the analyst',
  };

  readonly labels: { [key: string]: string } = {
    REQUIRED_BY_ANALYST_AND_CLIENT: 'Analyst / Client',
    REQUIRED_BY_CLIENT: 'Client',
    REQUIRED_BY_ANALYST: 'Analyst',
    REQUESTED_BY_ANALYST: 'Analyst',
  };

  iconName = '';
  tooltip = '';
  label = '';
  showPlusIcon = false;
  params: any;
  task!: Task;
  currentUser!: User;

  constructor(
    private userService: UserService,
    private tasksService: TasksService,
    private dialog: MatDialog,
  ) {}

  isQualityAnalyst(): boolean {
    return this.userService
      .getCurrentUser()
      ?.roles!.includes(AnalystRoleEnum.QUALITY_ANALYST);
  }

  agInit(params: any): void {
    this.params = params;
    this.task = params.data;
    this.updateIconState();
  }

  updateIconState(): void {
    this.tooltip = this.determineTooltip();
    this.label = this.determineLabel();
    this.iconName = this.determineIconName();
    this.showPlusIcon = this.canShowPlusIcon();
  }

  determineTooltip(): string {
    if (this.isRequestedByQualityAnalyst()) {
      return 'A quality check has been requested by the quality analyst';
    } else if (!!this.task.qualityRequestOrigin) {
      return this.tooltips[this.task.qualityRequestOrigin];
    } else {
      return '';
    }
  }

  isRequestedByQualityAnalyst(): boolean {
    return (
      this.task.qualityRequestedByQualityAnalyst! && this.isQualityAnalyst()
    );
  }

  determineLabel(): string {
    if (this.isRequestedByQualityAnalyst()) {
      return 'QA';
    } else if (
      this.isRequestedOrRequiredByAnalyst() &&
      this.task.qualityRequestedByQualityAnalyst &&
      this.isQualityAnalyst()
    ) {
      return 'QA/Analyst';
    } else if (!!this.task.qualityRequestOrigin) {
      return this.labels[this.task.qualityRequestOrigin!];
    } else {
      return '';
    }
  }

  private isRequestedOrRequiredByAnalyst(): boolean {
    return (
      this.task.qualityRequestOrigin ===
        QualityRequestOrigin.REQUIRED_BY_ANALYST_AND_CLIENT ||
      this.task.qualityRequestOrigin ===
        QualityRequestOrigin.REQUIRED_BY_ANALYST ||
      this.task.qualityRequestOrigin ===
        QualityRequestOrigin.REQUESTED_BY_ANALYST
    );
  }

  determineIconName(): string {
    if (
      this.isRequestedByQualityAnalyst() ||
      this.task.qualityRequestOrigin! ===
        QualityRequestOrigin.REQUESTED_BY_ANALYST
    ) {
      return 'mark_chat_read';
    } else if (!!this.task.qualityRequestOrigin) {
      return 'verified_user';
    } else {
      return '';
    }
  }

  refresh(): boolean {
    return false;
  }

  onPlusIconClick(): void {
    this.task.qualityRequestedByQualityAnalyst = true;
    this.updateIconState();

    this.tasksService
      .addAnalystQualityCheck(this.task.id!)
      .pipe(first())
      .subscribe();
  }

  onIconClick(): void {
    this.openConfirmDialog();
  }

  openConfirmDialog(): void {
    const dialogRef = this.dialog.open(ConfirmQualityDialogComponent, {
      width: '400px',
      data: {
        title: 'Remove Quality Check',
        message: 'Remove the quality check requested by the quality analyst?',
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (result === true) {
          this.removeQualityCheck();
        }
      });
  }

  removeQualityCheck(): void {
    this.task.qualityRequestedByQualityAnalyst = false;
    this.updateIconState();

    this.tasksService
      .removeAnalystQualityCheck(this.task.id!)
      .pipe(first())
      .subscribe();
  }

  canShowPlusIcon(): boolean {
    return (
      this.isQualityAnalyst() &&
      (this.task.taskType === 'QUALITY' ||
        this.task.taskType === 'WCL_QUALITY') &&
      this.task.taskStatus!.name === 'TODO_MAYBE' &&
      !this.task.qualityRequestedByQualityAnalyst
    );
  }
}
